import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import * as React from "react";

import type {
  DealStatus,
  InsightsPeriod,
  ManagerDealListItem,
  ManagerDealSegment,
} from "@/api/client";
import { getTeamDeals } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DealAdvancedFilters } from "@/components/deals/DealAdvancedFilters";
import {
  EMPTY_DEAL_FILTERS,
  matchesDealFilters,
  type DealAdvancedFilterState,
} from "@/components/deals/dealFilters";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import { PeriodToggle } from "@/components/insights/PeriodToggle";
import { PoDealLedger } from "@/components/po-insights/PoDealLedger";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormError } from "@/components/ui/form-error";
import { formatAudCents } from "@/lib/spif";

/** Which metric card opened the drawer — decides the initial filter focus.
 * `registrations` lands on the ACTIVE/live view (M3 — spec: WHERE
 * status='Active'); `expiring` auto-narrows to LIVE deals closing within 30
 * days; `closed` defaults to the Closed view (Won sub-toggle + won-rate
 * scorecards). */
export type DealExplorerFilter = "registrations" | "expiring" | "closed" | "pipeline";

/** The drawer's deal-feed source. The DEFAULT preserves exact PO behaviour:
 * `getTeamDeals` over the PO's own organisation. The Program Insights page
 * (CM/RM/RA) passes the program-wide `listManagerDeals` (/deals/manager-list)
 * instead, so the same drawer renders the whole channel rather than one org.
 * `queryKeyScope` keeps the two feeds in separate React-Query caches. */
export interface DealExplorerDataSource {
  /** Fetch the deal feed (segment is always "all" — the drawer filters
   * client-side). Default: getTeamDeals. */
  fetchDeals: (token: string, segment: ManagerDealSegment) => Promise<ManagerDealListItem[]>;
  /** A stable cache-scope label so program-wide vs org-scoped feeds don't
   * collide in the query cache. The PO default is "team-list" so the resulting
   * key `["deals","team-list","all",token]` stays byte-identical to the cache
   * the PO page / TeamDirectory / DealMetricDrawer already share. */
  queryKeyScope: string;
}

/** PO default — the org-scoped team feed. Exported so the PO page need not
 * restate it; omitting `dataSource` yields exactly this. The query key it
 * produces matches the pre-existing PO cache exactly. */
export const PO_DEAL_EXPLORER_SOURCE: DealExplorerDataSource = {
  fetchDeals: (token, segment) => getTeamDeals(token, segment),
  queryKeyScope: "team-list",
};

/** Program-wide Closed-Deals scorecards, sourced from the Insights dashboard's
 * already-computed InsightsDealsRow.closed_* fields. When supplied, the Closed
 * view renders these three spec cards (Closed Won FY / Closed Won MTD / Closed
 * Lost MTD) AND shows won + lost together in one ledger — replacing the PO
 * client-computed Won/Closed-value/Win-rate cards + Won/Declined sub-toggle.
 * Omitted on the PO surface, which keeps its existing behaviour. Monetary in
 * cents (divide by 100 for display). */
export interface ClosedDealsMetrics {
  closed_won_ytd_value_cents: number;
  closed_won_mtd_count: number;
  closed_won_mtd_value_cents: number;
  closed_lost_mtd_count: number;
  closed_lost_mtd_value_cents: number;
}

export interface DealExplorerDrawerProps {
  /** Open when true; false closes the drawer. */
  open: boolean;
  /** Fired when the drawer requests to close (overlay click / Esc / X). */
  onClose: () => void;
  /** Which card opened the drawer — sets the initial status filter. Defaults
   * to "registrations" (full pipeline). */
  initialFilter?: DealExplorerFilter;
  /** Where the deal feed comes from. Defaults to the PO org-scoped feed so the
   * PO Insights page is unchanged; the program page overrides it. */
  dataSource?: DealExplorerDataSource;
  /** Program-mode Closed-view scorecards from the dashboard's closed_* fields.
   * When set, the Closed view shows the three spec cards + won/lost together;
   * when omitted (PO surface) the legacy Won/Declined behaviour is preserved. */
  closedMetrics?: ClosedDealsMetrics;
}

/** The explorer's status-filter vocabulary (spec: Active / In Review /
 * Closed), plus the entry-specific "Expiring" focus. "all" is the [View All]
 * reset. These map onto the row `deal_status` client-side over the single
 * `getTeamDeals(token, "all")` fetch — no extra round-trips. */
type ExplorerFilter = "all" | "active" | "in_review" | "closed" | "expiring";

const EXPIRING_WINDOW_DAYS = 30;
const SEARCH_DEBOUNCE_MS = 250;

/** The Closed view's Won/Declined sub-toggle (M4). "won" = closed_won only;
 * "declined" = closed_lost + denied. Declined deals never won, so they're
 * grouped under one honest label. */
type ClosedSubview = "won" | "declined";

/** Status sets behind each filter (M4: Closed defaults to closed_won ONLY; the
 * Declined sub-view picks up closed_lost + denied). "all" keeps everything the
 * org-scoped feed returns (drafts are already excluded server-side). */
const FILTER_STATUSES: Record<Exclude<ExplorerFilter, "expiring">, readonly DealStatus[]> = {
  all: [
    "pending_review",
    "live",
    "extension_pending",
    "win_pending_review",
    "closed_won",
    "closed_lost",
    "denied",
  ],
  active: ["live"],
  in_review: ["pending_review", "extension_pending", "win_pending_review"],
  closed: ["closed_won"],
};

/** Declined = lost + denied (the Closed view's "Declined" sub-toggle). */
const DECLINED_STATUSES: readonly DealStatus[] = ["closed_lost", "denied"];

/** The status tabs (M7: shared Tabs idiom). "all" is the [View All] reset; the
 * Expiring entry view is a derived state OUTSIDE the tab set. */
const FILTER_TABS: { value: Exclude<ExplorerFilter, "expiring">; label: string }[] = [
  { value: "all", label: "View All" },
  // CHP review-r2 (#4cc8f386): "active" → "Live" everywhere across all users.
  // The filter value stays "active" (internal key for the live-status set);
  // only the user-facing label changes to match the deal-status vocabulary
  // ("Live"), which the status badges already use.
  { value: "active", label: "Live" },
  { value: "in_review", label: "In Review" },
  { value: "closed", label: "Closed" },
];

/** Whole-dollar AUD from cents — shared helper (m6). */
const formatCents = formatAudCents;

/** A LIVE deal closing within EXPIRING_WINDOW_DAYS of `now`. Deals with no
 * target close date can't be "expiring soon"; past-dated live deals still
 * count (they're overdue, which is the same risk signal). */
function isExpiringSoon(deal: ManagerDealListItem, now: Date): boolean {
  if (deal.deal_status !== "live" || !deal.target_close_date) return false;
  const close = new Date(deal.target_close_date).getTime();
  if (Number.isNaN(close)) return false;
  const horizon = now.getTime() + EXPIRING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return close <= horizon;
}

/** Lower bound for a period toggle value (program mode). "month"/"quarter"/
 * "year" → start of the current calendar month/quarter/year; null = no bound. */
function periodStart(period: Exclude<InsightsPeriod, "all">, now: Date): Date {
  if (period === "year") return new Date(now.getFullYear(), 0, 1);
  if (period === "quarter") {
    const qMonth = Math.floor(now.getMonth() / 3) * 3;
    return new Date(now.getFullYear(), qMonth, 1);
  }
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

/** Does a deal fall inside the period window? Closed deals window on closed_at
 * (falling back to the decision/update timestamp); open/pipeline deals window on
 * submission_timestamp (falling back to updated_at). A deal with no usable date
 * is retained (we can't prove it's outside the window). */
function inPeriod(deal: ManagerDealListItem, start: Date): boolean {
  const isClosed =
    deal.deal_status === "closed_won" ||
    deal.deal_status === "closed_lost" ||
    deal.deal_status === "denied";
  const raw = isClosed
    ? (deal.closed_at ?? deal.reviewed_at ?? deal.updated_at)
    : (deal.submission_timestamp ?? deal.updated_at);
  if (!raw) return true;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return true;
  return t >= start.getTime();
}

/** Translate the card that opened the drawer into the starting filter.
 * M3: "registrations" maps to the ACTIVE/live view (spec: WHERE status='Active'),
 * not the whole pipeline. [View All] is still one click away. */
function initialExplorerFilter(initial: DealExplorerFilter | undefined): ExplorerFilter {
  switch (initial) {
    case "expiring":
      return "expiring";
    case "closed":
      return "closed";
    case "pipeline":
      // The Pipeline Value card opens the whole open pipeline (View All) — a
      // distinct landing from the Registrations card's Active/live view.
      return "all";
    case "registrations":
    default:
      return "active";
  }
}

/**
 * Deal Explorer — the org-wide deal ledger behind the PO Insights "Deal
 * Registrations" / "Expiring Deals" cards (Insights & Admin Flow B/C).
 *
 * Loads the Program Owner's whole-organisation deal feed once
 * (`getTeamDeals(token, "all")`) and filters/searches client-side. Three
 * summary cards adapt to the active view: Volume / Value / Risk for the open
 * pipeline, Won / Closed value / Win-rate when Closed is selected. Row click
 * opens the master-record detail (`DealDetailDrawer`) layered over the
 * explorer; closing it returns here.
 *
 * The page (routes/dashboard/partner/insights) owns open/close; it passes
 * `initialFilter` from the clicked card. Edits to that page are limited to the
 * prop pass-through.
 */
export function DealExplorerDrawer({
  open,
  onClose,
  initialFilter,
  dataSource = PO_DEAL_EXPLORER_SOURCE,
  closedMetrics,
}: DealExplorerDrawerProps) {
  const { accessToken } = useCurrentUser();
  // Program mode (the Insights page passes the dashboard's closed_* metrics):
  // the Closed view shows the three spec scorecards + won/lost together, with
  // no Won/Declined sub-toggle. PO mode (closedMetrics undefined) is unchanged.
  const programClosed = closedMetrics !== undefined;

  const dealsQuery = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", dataSource.queryKeyScope, "all", accessToken],
    queryFn: () => dataSource.fetchDeals(accessToken!, "all"),
    enabled: !!accessToken && open,
  });

  const [filter, setFilter] = React.useState<ExplorerFilter>(() =>
    initialExplorerFilter(initialFilter),
  );
  // The Closed view's Won/Declined sub-toggle (M4). Defaults to "won".
  const [closedSubview, setClosedSubview] = React.useState<ClosedSubview>("won");
  // Program-mode period toggle (default This Month, re-seeded each open).
  const [period, setPeriod] = React.useState<Exclude<InsightsPeriod, "all">>("month");
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  // CHP-1051: Advanced Filters over the already-fetched feed — same client-side
  // reuse as DealMetricDrawer; no new API call.
  const [filters, setFilters] = React.useState<DealAdvancedFilterState>(EMPTY_DEAL_FILTERS);
  const [detailDealId, setDetailDealId] = React.useState<string | null>(null);

  // Re-seed the filter each time the drawer opens (or the entry card changes)
  // so the "Expiring Deals" card always lands on the expiring view even after
  // the PO last left it on Closed. Resets the search box + closed sub-toggle too.
  React.useEffect(() => {
    if (open) {
      setFilter(initialExplorerFilter(initialFilter));
      setClosedSubview("won");
      setPeriod("month");
      setSearchInput("");
      setDebouncedSearch("");
      setFilters(EMPTY_DEAL_FILTERS);
    }
  }, [open, initialFilter]);

  React.useEffect(() => {
    if (searchInput === "") {
      setDebouncedSearch("");
      return;
    }
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Single "now" per render so the expiring window is stable across the
  // summary cards and the row filter.
  const now = React.useMemo(() => new Date(), []);

  // Program mode applies the period toggle window to the whole feed before any
  // status filtering; PO mode is unwindowed (no period toggle). Closed cards in
  // program mode come from the dashboard's own MTD/FY metrics, so the period
  // window deliberately scopes only the LEDGER rows there, not those cards.
  const rawDeals = React.useMemo(() => dealsQuery.data ?? [], [dealsQuery.data]);
  const allDeals = React.useMemo(() => {
    if (!programClosed) return rawDeals;
    const start = periodStart(period, now);
    return rawDeals.filter((d) => inPeriod(d, start));
  }, [rawDeals, programClosed, period, now]);

  const query = debouncedSearch.trim().toLowerCase();

  // Status set for the active view. In the Closed view the Won/Declined
  // sub-toggle swaps the set (M4): "won" → closed_won, "declined" → lost+denied.
  // Program mode (closedMetrics supplied) shows closed_won + closed_lost
  // together (spec: won & lost in one ledger), ignoring the sub-toggle.
  const activeStatuses = React.useMemo<readonly DealStatus[]>(() => {
    if (filter === "closed") {
      if (programClosed) return ["closed_won", "closed_lost"];
      return closedSubview === "declined" ? DECLINED_STATUSES : FILTER_STATUSES.closed;
    }
    return filter === "expiring" ? [] : FILTER_STATUSES[filter];
  }, [filter, closedSubview, programClosed]);

  // Rows for the active view (pre-search). M4: Closed sorts by close date DESC —
  // ManagerDealListItem carries no `closed_at`, so the decision timestamp
  // (reviewed_at ?? updated_at) is the best "closed" proxy, falling back to the
  // target close date. Most-recently-closed first.
  const filteredForSummary = React.useMemo(() => {
    const rows =
      filter === "expiring"
        ? allDeals.filter((d) => isExpiringSoon(d, now))
        : allDeals.filter((d) => activeStatuses.includes(d.deal_status));
    if (filter !== "closed") return rows;
    const closeKey = (d: ManagerDealListItem) =>
      d.reviewed_at ?? d.updated_at ?? d.target_close_date ?? "";
    return [...rows].sort((a, b) => closeKey(b).localeCompare(closeKey(a)));
  }, [allDeals, filter, activeStatuses, now]);

  const visibleDeals = React.useMemo(() => {
    // CHP-1051: advanced filters compose (AND) with the status view + search,
    // over the rows already fetched (field mapping mirrors DealMetricDrawer).
    const filtered = filteredForSummary.filter((d) =>
      matchesDealFilters(filters, {
        submittedAt: d.submission_timestamp,
        closingAt: d.target_close_date,
        valueCents: d.total_contract_value,
        partnerOrg: d.channel_partner_company,
        owner: d.lead_bdm_name,
      }),
    );
    if (query === "") return filtered;
    return filtered.filter(
      (d) =>
        d.client_name.toLowerCase().includes(query) ||
        (d.deal_id_display ?? "").toLowerCase().includes(query) ||
        (d.lead_bdm_name ?? "").toLowerCase().includes(query),
    );
  }, [filteredForSummary, filters, query]);

  // Owner options for the filters popover, derived from the loaded rows (same
  // derivation as DealMetricDrawer).
  const ownerOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const d of allDeals) if (d.lead_bdm_name) set.add(d.lead_bdm_name);
    return [...set].sort();
  }, [allDeals]);

  // Summary cards adapt to the view. Closed shows outcome scorecards (Won /
  // Closed value / Win-rate); the win-rate denominator is the WHOLE decided set
  // (won + lost + denied) so it stays meaningful regardless of the Won/Declined
  // sub-toggle. Every other view shows the open-pipeline trio (Volume / Value /
  // Risk), computed over the active view's rows (pre-search).
  const summary = React.useMemo(() => {
    // Program mode: the three spec scorecards straight from the dashboard's
    // closed_* fields (Closed Won YTD / Closed Won MTD / Closed Lost MTD).
    // YTD = calendar year per the CHP-814 rule decision.
    if (filter === "closed" && programClosed && closedMetrics) {
      return [
        {
          testId: "deal-explorer-summary-closed-won-ytd",
          title: "Closed Won (YTD)",
          value: formatCents(closedMetrics.closed_won_ytd_value_cents),
          subtitle: undefined as string | undefined,
        },
        {
          testId: "deal-explorer-summary-closed-won-mtd",
          title: "Closed Won (MTD)",
          value: formatCents(closedMetrics.closed_won_mtd_value_cents),
          subtitle: `${closedMetrics.closed_won_mtd_count} this month`,
        },
        {
          testId: "deal-explorer-summary-closed-lost-mtd",
          title: "Closed Lost (MTD)",
          value: formatCents(closedMetrics.closed_lost_mtd_value_cents),
          subtitle: `${closedMetrics.closed_lost_mtd_count} this month`,
        },
      ];
    }
    if (filter === "closed") {
      const decidedSet = allDeals.filter(
        (d) =>
          FILTER_STATUSES.closed.includes(d.deal_status) ||
          DECLINED_STATUSES.includes(d.deal_status),
      );
      const won = decidedSet.filter((d) => d.deal_status === "closed_won");
      const decided = decidedSet.length;
      const wonValue = won.reduce((sum, d) => sum + (d.total_contract_value ?? 0), 0);
      const winRate = decided > 0 ? Math.round((won.length / decided) * 100) : 0;
      return [
        {
          testId: "deal-explorer-summary-won",
          title: "Won",
          value: String(won.length),
          subtitle: undefined as string | undefined,
        },
        {
          testId: "deal-explorer-summary-closed-value",
          title: "Closed value",
          value: formatCents(wonValue),
          subtitle: undefined,
        },
        {
          testId: "deal-explorer-summary-win-rate",
          title: "Win rate",
          value: `${winRate}%`,
          subtitle: `${decided} decided`,
        },
      ];
    }
    const volume = filteredForSummary.length;
    const value = filteredForSummary.reduce((sum, d) => sum + (d.total_contract_value ?? 0), 0);
    const atRisk = filteredForSummary.filter((d) => isExpiringSoon(d, now)).length;
    return [
      {
        testId: "deal-explorer-summary-volume",
        title: "Volume",
        value: String(volume),
        subtitle: undefined as string | undefined,
      },
      {
        testId: "deal-explorer-summary-value",
        title: "Value",
        value: formatCents(value),
        subtitle: undefined,
      },
      {
        testId: "deal-explorer-summary-risk",
        title: "Risk",
        value: String(atRisk),
        subtitle: atRisk > 0 ? "expiring ≤30 days" : undefined,
      },
    ];
  }, [filter, allDeals, filteredForSummary, now, programClosed, closedMetrics]);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="deal-explorer-drawer"
      >
        <SheetHeader>
          <SheetTitle className="pr-8">Deal Performance</SheetTitle>
          <SheetDescription>
            {programClosed
              ? "Manage and track program-wide deal performance."
              : "Manage and track organisation-wide deal performance."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Summary scorecards — adapt to the active view (m10: explicit
              stable testIds, not title-slugged). */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {summary.map((card) => (
              <StatCard
                key={card.testId}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                tone="neutral"
                testId={card.testId}
              />
            ))}
          </div>

          {/* Status filter — shared Tabs idiom (M7), matching SpifEarningsDrawer
              / TeamTrainingDrawer / ManagerDealsLedger. The Expiring entry view
              is a derived state OUTSIDE the tab set (no active tab while it's on);
              the Closed view exposes a Won/Declined sub-toggle (M4). */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Tabs
                value={filter === "expiring" ? "" : filter}
                onValueChange={(next) => {
                  // The Expiring entry view sits outside the tab set (value="").
                  // Ignore the empty/no-op transition Radix can emit while no
                  // trigger matches; only honour a real tab selection.
                  if (next === "" || next === filter) return;
                  setFilter(next as ExplorerFilter);
                }}
                activationMode="manual"
                data-testid="deal-explorer-filter"
              >
                <TabsList>
                  {FILTER_TABS.map((t) => (
                    <TabsTrigger
                      key={t.value}
                      value={t.value}
                      data-testid={`deal-explorer-filter-${t.value}`}
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {/* Closed view's Won/Declined sub-toggle (M4). Hidden in program
                  mode, where won + lost render together in one ledger. */}
              {filter === "closed" && !programClosed ? (
                <Tabs
                  value={closedSubview}
                  onValueChange={(next) => setClosedSubview(next as ClosedSubview)}
                  data-testid="deal-explorer-closed-subview"
                >
                  <TabsList>
                    <TabsTrigger value="won" data-testid="deal-explorer-closed-won">
                      Won
                    </TabsTrigger>
                    <TabsTrigger value="declined" data-testid="deal-explorer-closed-declined">
                      Declined
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : null}

              {/* Expiring entry view — derived state outside the tab set. */}
              {filter === "expiring" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  data-testid="deal-explorer-filter-expiring"
                  aria-pressed
                >
                  Expiring ≤30d
                </Button>
              ) : null}

              {/* Program-mode period toggle (This Month default). PO mode has no
                  toggle (closedMetrics undefined → programClosed false). */}
              {programClosed ? (
                <PeriodToggle value={period} onChange={setPeriod} testId="deal-explorer-period" />
              ) : null}
            </div>

            <div className="flex w-full items-center gap-2 sm:w-auto">
              <div className="relative w-full sm:w-72">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by client, deal ID, or owner…"
                  aria-label="Search deals"
                  className="pl-9"
                  data-testid="deal-explorer-search"
                />
              </div>
              {/* CHP-1051: filters sit right of search (cross-page convention). */}
              <DealAdvancedFilters
                value={filters}
                onChange={setFilters}
                ownerOptions={ownerOptions}
              />
            </div>
          </div>

          {/* Org-scoped read-only ledger (parity D1/D2): six columns
              (Deal ID / Client / Owner / Value / Status / Expiry Date), no
              deal-adjudication action — a PO must never see the CM
              "Change status" control. Row click layers the master-record
              detail over this drawer. */}
          {dealsQuery.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading deals…
            </p>
          ) : dealsQuery.error ? (
            <FormError>
              {programClosed
                ? "Couldn't load the channel's deals."
                : "Couldn't load your organisation's deals."}
            </FormError>
          ) : (
            <PoDealLedger
              deals={visibleDeals}
              onDealClick={setDetailDealId}
              showPartnerColumn={programClosed}
              emptyMessage={
                query !== ""
                  ? "No deals match your search."
                  : programClosed
                    ? "No records found."
                    : "No deals in this view."
              }
            />
          )}
        </div>

        {/* Master-record detail, layered over the explorer. Closing returns
            here (the explorer stays open underneath). CHP-808: read-only — the
            viewer (a PO browsing the org's deals, or a CM/RM/RA browsing the
            channel) is never the deal owner, so the owner-only write actions
            (Log progress / Request Closed-Won / Mark Closed-Lost) are
            suppressed (the backend 404s a non-owner on those endpoints). */}
        <DealDetailDrawer dealId={detailDealId} onClose={() => setDetailDealId(null)} readOnly />
      </SheetContent>
    </Sheet>
  );
}
